import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Scale as ScaleIcon,
  FitnessCenter as FitnessCenterIcon,
  ExpandMore as ExpandMoreIcon,
  MonitorWeight as WeightIcon,
  Favorite as HeartIcon
} from '@mui/icons-material';
import api from '../utils/api';

const PhysicalMeasurementForm = ({ open, onClose, onSave, editingMeasurement = null }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    weight: '',
    height: '',
    bodyFatPercentage: '',
    muscleMass: '',
    visceralFat: '',
    measurements: {
      chest: '',
      waist: '',
      hips: '',
      thighs: '',
      arms: '',
      forearms: '',
      calves: '',
      neck: ''
    },
    restingHeartRate: '',
    bloodPressure: {
      systolic: '',
      diastolic: ''
    },
    flexibility: {
      sitAndReach: ''
    },
    strength: {
      gripStrength: ''
    },
    notes: '',
    measurementMethod: 'manual',
    isPublic: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bmi, setBmi] = useState(null);

  useEffect(() => {
    if (open) {
      if (editingMeasurement) {
        setFormData({
          ...editingMeasurement,
          measurements: editingMeasurement.measurements || {
            chest: '', waist: '', hips: '', thighs: '', arms: '', forearms: '', calves: '', neck: ''
          },
          bloodPressure: editingMeasurement.bloodPressure || { systolic: '', diastolic: '' },
          flexibility: editingMeasurement.flexibility || { sitAndReach: '' },
          strength: editingMeasurement.strength || { gripStrength: '' }
        });
      } else {
        resetForm();
      }
    }
  }, [open, editingMeasurement]);

  useEffect(() => {
    // Calculate BMI when weight and height change
    if (formData.weight && formData.height) {
      const weightKg = parseFloat(formData.weight);
      const heightM = parseFloat(formData.height) / 100;
      const calculatedBmi = weightKg / (heightM * heightM);
      setBmi(Math.round(calculatedBmi * 10) / 10);
    } else {
      setBmi(null);
    }
  }, [formData.weight, formData.height]);

  const resetForm = () => {
    setFormData({
      weight: '',
      height: '',
      bodyFatPercentage: '',
      muscleMass: '',
      visceralFat: '',
      measurements: {
        chest: '', waist: '', hips: '', thighs: '', arms: '', forearms: '', calves: '', neck: ''
      },
      restingHeartRate: '',
      bloodPressure: { systolic: '', diastolic: '' },
      flexibility: { sitAndReach: '' },
      strength: { gripStrength: '' },
      notes: '',
      measurementMethod: 'manual',
      isPublic: false
    });
    setError('');
    setBmi(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const getBmiCategory = (bmiValue) => {
    if (!bmiValue) return null;
    if (bmiValue < 18.5) return { key: 'underweight', color: 'info' };
    if (bmiValue < 25) return { key: 'normal', color: 'success' };
    if (bmiValue < 30) return { key: 'overweight', color: 'warning' };
    return { key: 'obese', color: 'error' };
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Basic validation
      if (!formData.weight && !formData.height && !formData.bodyFatPercentage) {
        setError(t('physicalMeasurement:validation.weightRequired'));
        return;
      }

      const measurementData = {
        ...formData,
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        height: formData.height ? parseFloat(formData.height) : undefined,
        bodyFatPercentage: formData.bodyFatPercentage ? parseFloat(formData.bodyFatPercentage) : undefined,
        muscleMass: formData.muscleMass ? parseFloat(formData.muscleMass) : undefined,
        visceralFat: formData.visceralFat ? parseFloat(formData.visceralFat) : undefined,
        restingHeartRate: formData.restingHeartRate ? parseInt(formData.restingHeartRate) : undefined,
        bloodPressure: {
          systolic: formData.bloodPressure.systolic ? parseInt(formData.bloodPressure.systolic) : undefined,
          diastolic: formData.bloodPressure.diastolic ? parseInt(formData.bloodPressure.diastolic) : undefined
        },
        measurements: Object.fromEntries(
          Object.entries(formData.measurements).map(([key, value]) => [
            key, value ? parseFloat(value) : undefined
          ])
        ),
        flexibility: {
          sitAndReach: formData.flexibility.sitAndReach ? parseFloat(formData.flexibility.sitAndReach) : undefined
        },
        strength: {
          gripStrength: formData.strength.gripStrength ? parseFloat(formData.strength.gripStrength) : undefined
        }
      };

      if (editingMeasurement) {
        await api.put(`/physical-measurements/${editingMeasurement._id}`, measurementData);
      } else {
        await api.post('/physical-measurements', measurementData);
      }

      onSave();
      onClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save measurement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <ScaleIcon color="primary" />
          <Typography variant="h6">
            {editingMeasurement ? t('physicalMeasurement:title.edit') : t('physicalMeasurement:title.add')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Basic Measurements */}
          <Grid size={12}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WeightIcon /> {t('physicalMeasurement:sections.basicMeasurements')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label={t('physicalMeasurement:labels.weight')}
              type="number"
              value={formData.weight}
              onChange={(e) => handleInputChange('weight', e.target.value)}
              inputProps={{ step: 0.1, min: 20, max: 300 }}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label={t('physicalMeasurement:labels.height')}
              type="number"
              value={formData.height}
              onChange={(e) => handleInputChange('height', e.target.value)}
              inputProps={{ step: 0.1, min: 100, max: 250 }}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label={t('physicalMeasurement:labels.bodyFat')}
              type="number"
              value={formData.bodyFatPercentage}
              onChange={(e) => handleInputChange('bodyFatPercentage', e.target.value)}
              inputProps={{ step: 0.1, min: 3, max: 50 }}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label={t('physicalMeasurement:labels.muscleMass')}
              type="number"
              value={formData.muscleMass}
              onChange={(e) => handleInputChange('muscleMass', e.target.value)}
              inputProps={{ step: 0.1, min: 10, max: 100 }}
            />
          </Grid>

          {/* BMI Display */}
          {bmi && (
            <Grid size={12}>
              <Alert 
                severity={getBmiCategory(bmi)?.color || 'info'} 
                sx={{ mt: 1 }}
              >
                <Typography variant="subtitle2">
                  {t('physicalMeasurement:labels.bmi')}: {bmi} - {t(`physicalMeasurement:bmiCategories.${getBmiCategory(bmi)?.key}`)}
                </Typography>
              </Alert>
            </Grid>
          )}

          {/* Body Measurements Accordion */}
          <Grid size={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FitnessCenterIcon /> {t('physicalMeasurement:sections.bodyMeasurements')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Chest"
                      type="number"
                      value={formData.measurements.chest}
                      onChange={(e) => handleNestedInputChange('measurements', 'chest', e.target.value)}
                      inputProps={{ step: 0.1, min: 50, max: 150 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Waist"
                      type="number"
                      value={formData.measurements.waist}
                      onChange={(e) => handleNestedInputChange('measurements', 'waist', e.target.value)}
                      inputProps={{ step: 0.1, min: 40, max: 150 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Hips"
                      type="number"
                      value={formData.measurements.hips}
                      onChange={(e) => handleNestedInputChange('measurements', 'hips', e.target.value)}
                      inputProps={{ step: 0.1, min: 50, max: 150 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Thighs"
                      type="number"
                      value={formData.measurements.thighs}
                      onChange={(e) => handleNestedInputChange('measurements', 'thighs', e.target.value)}
                      inputProps={{ step: 0.1, min: 30, max: 80 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Arms"
                      type="number"
                      value={formData.measurements.arms}
                      onChange={(e) => handleNestedInputChange('measurements', 'arms', e.target.value)}
                      inputProps={{ step: 0.1, min: 15, max: 50 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Forearms"
                      type="number"
                      value={formData.measurements.forearms}
                      onChange={(e) => handleNestedInputChange('measurements', 'forearms', e.target.value)}
                      inputProps={{ step: 0.1, min: 15, max: 40 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Calves"
                      type="number"
                      value={formData.measurements.calves}
                      onChange={(e) => handleNestedInputChange('measurements', 'calves', e.target.value)}
                      inputProps={{ step: 0.1, min: 20, max: 50 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Neck"
                      type="number"
                      value={formData.measurements.neck}
                      onChange={(e) => handleNestedInputChange('measurements', 'neck', e.target.value)}
                      inputProps={{ step: 0.1, min: 25, max: 50 }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Health Metrics Accordion */}
          <Grid size={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HeartIcon /> Health & Fitness Metrics
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Resting Heart Rate (bpm)"
                      type="number"
                      value={formData.restingHeartRate}
                      onChange={(e) => handleInputChange('restingHeartRate', e.target.value)}
                      inputProps={{ min: 40, max: 120 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Systolic BP (mmHg)"
                      type="number"
                      value={formData.bloodPressure.systolic}
                      onChange={(e) => handleNestedInputChange('bloodPressure', 'systolic', e.target.value)}
                      inputProps={{ min: 80, max: 200 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Diastolic BP (mmHg)"
                      type="number"
                      value={formData.bloodPressure.diastolic}
                      onChange={(e) => handleNestedInputChange('bloodPressure', 'diastolic', e.target.value)}
                      inputProps={{ min: 40, max: 120 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Visceral Fat Level"
                      type="number"
                      value={formData.visceralFat}
                      onChange={(e) => handleInputChange('visceralFat', e.target.value)}
                      inputProps={{ step: 0.1, min: 1, max: 30 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Sit & Reach (cm)"
                      type="number"
                      value={formData.flexibility.sitAndReach}
                      onChange={(e) => handleNestedInputChange('flexibility', 'sitAndReach', e.target.value)}
                      inputProps={{ step: 0.1, min: -20, max: 50 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Grip Strength (kg)"
                      type="number"
                      value={formData.strength.gripStrength}
                      onChange={(e) => handleNestedInputChange('strength', 'gripStrength', e.target.value)}
                      inputProps={{ step: 0.1, min: 10, max: 100 }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Measurement Settings */}
          <Grid size={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Measurement Settings
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Measurement Method</InputLabel>
              <Select
                value={formData.measurementMethod}
                label="Measurement Method"
                onChange={(e) => handleInputChange('measurementMethod', e.target.value)}
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="bioelectrical_impedance">Bioelectrical Impedance</MenuItem>
                <MenuItem value="dexa_scan">DEXA Scan</MenuItem>
                <MenuItem value="bod_pod">Bod Pod</MenuItem>
                <MenuItem value="caliper">Caliper</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPublic}
                  onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                />
              }
              label="Share with trainers"
            />
          </Grid>

          {/* Notes */}
          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Any additional notes about your measurements..."
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Saving...' : (editingMeasurement ? 'Update Measurement' : 'Save Measurement')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhysicalMeasurementForm;